import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import faviconAvoprime from '../assets/images/favicon-avoprime.svg';
import { useSmoothScroll } from '../hooks/useSmoothScroll';
import ContactModal from './ContactModal';
import LanguageSelector from './LanguageSelector';
import MobileLanguageSelector from './MobileLanguageSelector';

const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { scrollToSection } = useSmoothScroll();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Detectar seção ativa
      const sections = [
        'hero',
        'about-company',
        'harvest',
        'why-choose',
        'product-avoprime',
        'about-hass',
        'ripeness-guide',
        'comparison',
        'market-opportunity',
        'contact',
      ];

      const productSections = ['product-avoprime', 'about-hass', 'ripeness-guide', 'comparison'];

      const companySections = ['about-company', 'harvest', 'why-choose'];

      const marketSections = ['market-opportunity'];

      const scrollPosition = window.scrollY + 100; // offset para melhor detecção

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            if (productSections.includes(sectionId)) {
              setActiveSection('product-avoprime');
            } else if (companySections.includes(sectionId)) {
              setActiveSection('about-company');
            } else if (marketSections.includes(sectionId)) {
              setActiveSection('market-opportunity');
            } else {
              setActiveSection(sectionId);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToSection = (sectionId: string) => {
    scrollToSection(sectionId);
    setIsMenuOpen(false);
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
    setIsMenuOpen(false);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const navigationItems = [
    { name: t('navigation.aboutUs'), href: 'about-company' },
    { name: t('navigation.product'), href: 'product-avoprime' },
    { name: t('navigation.market'), href: 'market-opportunity' },
  ];

  return (
    <>
      <header
        className={`fixed p-3 top-0 left-0 right-0 z-40 transition-all duration-300  ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className='container mx-auto px-4 sm:px-6 lg:px-8 relative'>
          <div className='flex items-center justify-between h-20 relative'>
            {/* Logo - Esquerda */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className='flex items-center space-x-2 cursor-pointer z-10'
              onClick={() => handleScrollToSection('hero')}
            >
              {/* Favicon */}
              <img
                src={faviconAvoprime}
                alt='Avoprime Icon'
                className='h-[3.5rem] w-[3.5rem]'
              />

              {/* Texto AVOPrime */}
              <div className='flex items-center'>
                <span
                  className={`text-4xl font-eras-light tracking-wider ${
                    isScrolled ? 'text-avoprime-dark' : 'text-white'
                  }`}
                >
                  AVO
                </span>
                <span
                  className={`text-4xl font-eras-regular ${
                    isScrolled ? 'text-avoprime-dark' : 'text-white'
                  }`}
                >
                  Prime
                </span>
              </div>
            </motion.div>

            {/* Desktop Navigation - Centro Absoluto */}
            <nav className='hidden lg:flex items-center absolute left-1/2 transform -translate-x-1/2 space-x-8'>
              {navigationItems.map((item, index) => (
                <motion.button
                  key={item.name}
                  onClick={() => handleScrollToSection(item.href)}
                  className={`font-montserrat text-xl font-medium transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-0 relative active:scale-100 select-none ${
                    activeSection === item.href
                      ? isScrolled
                        ? 'text-avoprime-primary'
                        : 'text-white'
                      : isScrolled
                        ? 'text-gray-700 hover:text-avoprime-primary'
                        : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.name}
                  {activeSection === item.href && (
                    <motion.div
                      className={`absolute -bottom-1 left-0 right-0 h-0.5 ${
                        isScrolled ? 'bg-avoprime-primary' : 'bg-white'
                      }`}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </motion.button>
              ))}
            </nav>

            {/* CTA Button e Language Selector - Direita */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className='hidden lg:flex items-center space-x-4 z-10'
            >
              {/* Language Selector */}
              <LanguageSelector isScrolled={isScrolled} />

              {/* CTA Button */}
              <button
                onClick={openContactModal}
                className={`px-6 text-xl py-3 rounded-full font-eras font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-0 active:scale-100 select-none ${
                  isScrolled
                    ? 'bg-avoprime-primary text-white hover:bg-avoprime-secondary'
                    : 'bg-white text-avoprime-primary hover:bg-avoprime-light'
                }`}
              >
                {t('header.cta')}
              </button>
            </motion.div>

            {/* Mobile Menu Button */}
            <div className='lg:hidden'>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-lg transition-colors duration-200 ${
                  isScrolled
                    ? 'text-gray-700 hover:text-avoprime-primary'
                    : 'text-white hover:text-avoprime-light'
                }`}
              >
                {isMenuOpen ? (
                  <XMarkIcon className='w-8 h-8' />
                ) : (
                  <Bars3Icon className='w-8 h-8' />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <motion.div
            initial={false}
            animate={{
              opacity: isMenuOpen ? 1 : 0,
              height: isMenuOpen ? 'auto' : 0,
              display: isMenuOpen ? 'block' : 'none',
            }}
            transition={{ duration: 0.3 }}
            className='lg:hidden overflow-hidden bg-white/95 backdrop-blur-md shadow-lg absolute top-full mt-2 z-50 rounded-b-3xl'
            style={{
              left: '0',
              right: '0',
              width: '100vw',
              marginLeft: 'calc(-50vw + 50%)',
              marginRight: 'calc(-50vw + 50%)',
            }}
          >
            <div className='py-6 space-y-4 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
              {navigationItems.map(item => (
                <button
                  key={item.name}
                  onClick={() => handleScrollToSection(item.href)}
                  className={`block w-full text-left px-6 py-3 transition-all duration-200 font-montserrat font-medium relative focus:outline-none focus:ring-0 active:scale-100 select-none ${
                    activeSection === item.href
                      ? 'text-avoprime-primary bg-avoprime-primary/10'
                      : 'text-gray-700 hover:text-avoprime-primary hover:bg-avoprime-primary/5'
                  }`}
                >
                  {item.name}
                </button>
              ))}

              {/* Mobile Language Selector */}
              <div className='px-6 py-3'>
                <MobileLanguageSelector isScrolled={isScrolled} />
              </div>

              <div className='px-6 pt-4'>
                <button
                  onClick={openContactModal}
                  className='w-full bg-gradient-to-r from-avoprime-primary to-avoprime-secondary text-white font-eras font-bold px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-0 active:scale-100 select-none'
                >
                  {t('header.cta')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Contact Modal */}
      <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />
    </>
  );
};

export default Header;
